/**
 * ScanSummaryModal.tsx
 * Shows a comparison of scanned vs expected totals per item before
 * the supervisor proceeds to the decision step.  Highlights discrepancies.
 */
import React from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { TransferItem } from '../../../services/inventoryService';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  ClipboardCheck,
} from 'lucide-react';

export interface ScannedBox {
  gtin: string;
  weight: number;
  adjustedWeight?: number;
  quantity?: number;
  is_packing_material?: boolean;
  item_number?: string;
  item_name?: string;
  status: 'accepted' | 'rejected' | 'adjusted' | 'on_hold';
  notInTransfer?: boolean;
}

interface ScanSummaryModalProps {
  isOpen: boolean;
  scannedBoxes: ScannedBox[];
  transferItems: TransferItem[];
  onConfirm: () => void;
  onGoBack: () => void;
}

const ScanSummaryModal: React.FC<ScanSummaryModalProps> = ({
  isOpen,
  scannedBoxes,
  transferItems,
  onConfirm,
  onGoBack,
}) => {
  const { t } = useLanguage();

  if (!isOpen) return null;

  // Only consider pending (not already received) items
  const pendingItems = transferItems.filter(i => !i.already_received);

  // Check if any item is a packing material (to show pcs column)
  const hasPacking = pendingItems.some(i => i.is_packing_material === 'Y');

  // Build per-item comparison
  const itemRows = pendingItems.map(item => {
    const matchingBoxes = scannedBoxes.filter(
      b => b.item_number === item.item_number && !b.notInTransfer
    );
    const isPacking = item.is_packing_material === 'Y';
    const scannedQty = isPacking
      ? matchingBoxes.reduce((sum, b) => sum + (b.quantity || 1), 0)
      : matchingBoxes.length;
    // WCO-3121: `??` doesn't catch NaN — coerce each box weight to a finite number so a legacy
    // restored NaN/null box can't turn the whole scanned-weight total into NaN.
    const rawScannedWeight = matchingBoxes.reduce((sum, b) => {
      const w = b.adjustedWeight ?? b.weight;
      return sum + (Number.isFinite(w) ? (w as number) : 0);
    }, 0);
    const unitWeight = Number(item.unit_weight) || 0;
    const subQty = Number(item.item_sub_quantity) || 0;
    // For packing materials, compute weight from unit_weight × scannedQty
    const scannedWeight = isPacking && rawScannedWeight === 0
      ? scannedQty * unitWeight
      : rawScannedWeight;
    const expectedPcs = subQty > 0 ? item.expected_quantity * subQty : 0;
    const scannedPcs = subQty > 0 ? scannedQty * subQty : 0;
    const qtyDiff = scannedQty - item.expected_quantity;
    const weightDiff = scannedWeight - item.expected_weight;

    return {
      item_number: item.item_number,
      item_name: item.item_name,
      isPacking,
      subQty,
      expectedQty: item.expected_quantity,
      expectedWeight: item.expected_weight,
      scannedQty,
      scannedWeight,
      expectedPcs,
      scannedPcs,
      qtyDiff,
      weightDiff,
      hasDiscrepancy: isPacking ? qtyDiff !== 0 : (qtyDiff !== 0 || Math.abs(weightDiff) >= 0.5),
    };
  });

  // "Not in transfer" boxes
  const notInTransferBoxes = scannedBoxes.filter(b => b.notInTransfer);

  // Totals — separate packing vs raw material items
  const rawRows = itemRows.filter(r => !r.isPacking);
  const totalExpectedQty = itemRows.reduce((s, r) => s + r.expectedQty, 0);
  const totalScannedQty = itemRows.reduce((s, r) => s + r.scannedQty, 0);
  const totalScannedWeight = itemRows.reduce((s, r) => s + r.scannedWeight, 0);
  const totalExpectedWeight = itemRows.reduce((s, r) => s + r.expectedWeight, 0);
  const totalScannedPcs = itemRows.reduce((s, r) => s + r.scannedPcs, 0);
  const totalQtyDiff = totalScannedQty - totalExpectedQty;
  const totalWeightDiff = totalScannedWeight - totalExpectedWeight;

  const hasAnyDiscrepancy = itemRows.some(r => r.hasDiscrepancy) || notInTransferBoxes.length > 0;

  // Format variance with sign
  const fmtVar = (v: number, decimals = 0) => {
    const sign = v > 0 ? '+' : '';
    return `${sign}${decimals > 0 ? v.toFixed(decimals) : v}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
          <ClipboardCheck className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('transferVerification.scanSummary')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('transferVerification.scanSummarySubtitle')}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-4 space-y-4">
          {/* Discrepancy banner */}
          {hasAnyDiscrepancy ? (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">{t('transferVerification.discrepancyWarning')}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-300">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">{t('transferVerification.noDiscrepancies')}</span>
            </div>
          )}

          {/* Per-item table */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700 text-xs font-medium text-gray-600 dark:text-gray-300 uppercase">
                  <th className="text-left px-2 py-2">{t('transferVerification.item')}</th>
                  <th className="text-center px-1 py-2">{t('transferVerification.expected')}</th>
                  <th className="text-center px-1 py-2">{t('transferVerification.scanned')}</th>
                  {hasPacking && <th className="text-center px-1 py-2">PCS</th>}
                  <th className="text-right px-2 py-2">lbs</th>
                </tr>
              </thead>
              <tbody>
                {itemRows.map(row => (
                  <React.Fragment key={row.item_number}>
                    {(() => {
                      const boxOk = row.scannedQty >= row.expectedQty;
                      const weightOk = row.scannedWeight >= row.expectedWeight;
                      const bgClass = row.hasDiscrepancy
                        ? (boxOk && weightOk) ? 'bg-green-50 dark:bg-green-900/10'
                          : (!boxOk && !weightOk) ? 'bg-red-50 dark:bg-red-900/10'
                          : 'bg-amber-50 dark:bg-amber-900/10'
                        : '';
                      const boxColor = boxOk ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
                      const weightColor = weightOk ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
                      return (
                        <>
                          <tr className={`border-t border-gray-100 dark:border-gray-700 ${bgClass}`}>
                            <td className="px-2 py-2">
                              <span className="font-medium text-gray-800 dark:text-gray-200 text-xs block truncate max-w-[140px]">
                                {row.item_name || row.item_number}
                              </span>
                              <span className="text-[10px] text-gray-500 dark:text-gray-400">{row.item_number}</span>
                            </td>
                            <td className="text-center px-1 py-2 text-gray-700 dark:text-gray-300">
                              {row.expectedQty}
                            </td>
                            <td className="text-center px-1 py-2 text-gray-700 dark:text-gray-300">
                              {row.scannedQty}
                            </td>
                            {hasPacking && (
                              <td className="text-center px-1 py-2 text-gray-700 dark:text-gray-300">
                                {row.isPacking && row.subQty > 0 ? row.scannedPcs : '-'}
                              </td>
                            )}
                            <td className="text-right px-2 py-2 text-gray-700 dark:text-gray-300 font-mono">
                              {row.scannedWeight.toFixed(2)}
                            </td>
                          </tr>
                          {row.hasDiscrepancy && (
                            <tr className={bgClass}>
                              <td colSpan={hasPacking ? 5 : 4} className="px-2 pb-2 text-right text-xs font-medium">
                                {row.qtyDiff !== 0 && (
                                  <span className={boxColor}>
                                    {fmtVar(row.qtyDiff)} {row.isPacking ? t('transferVerification.qty') : t('transferVerification.boxes')}
                                  </span>
                                )}
                                {!row.isPacking && Math.abs(row.weightDiff) >= 0.01 && (
                                  <span className={weightColor}>{row.qtyDiff !== 0 ? ' · ' : ''}{fmtVar(row.weightDiff, 2)} lbs</span>
                                )}
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })()}
                  </React.Fragment>
                ))}
              </tbody>
              {/* Totals row — only show when multiple items */}
              {itemRows.length > 1 && <tfoot>
                <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 font-semibold">
                  <td className="px-2 py-2 text-gray-800 dark:text-gray-200">Total</td>
                  <td className="text-center px-1 py-2 text-gray-800 dark:text-gray-200">{totalExpectedQty}</td>
                  <td className="text-center px-1 py-2 text-gray-800 dark:text-gray-200">{totalScannedQty}</td>
                  {hasPacking && (
                    <td className="text-center px-1 py-2 text-gray-800 dark:text-gray-200">
                      {totalScannedPcs > 0 ? totalScannedPcs : '-'}
                    </td>
                  )}
                  <td className="text-right px-2 py-2 text-gray-800 dark:text-gray-200 font-mono">
                    {totalScannedWeight.toFixed(2)}
                  </td>
                </tr>
                {(totalQtyDiff !== 0 || Math.abs(totalWeightDiff) >= 0.01) && (
                  <tr className="bg-gray-50 dark:bg-gray-700/50">
                    <td colSpan={hasPacking ? 5 : 4} className="px-2 pb-2 text-right text-xs font-semibold">
                      {totalQtyDiff !== 0 && (
                        <span className={totalScannedQty >= totalExpectedQty ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                          {fmtVar(totalQtyDiff)} {t('transferVerification.boxes')}
                        </span>
                      )}
                      {Math.abs(totalWeightDiff) >= 0.01 && (
                        <span className={totalScannedWeight >= totalExpectedWeight ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                          {totalQtyDiff !== 0 ? ' · ' : ''}{fmtVar(totalWeightDiff, 2)} lbs
                        </span>
                      )}
                    </td>
                  </tr>
                )}
              </tfoot>}
            </table>
          </div>

          {/* Not-in-transfer boxes */}
          {notInTransferBoxes.length > 0 && (
            <div className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
              <p className="text-sm font-medium text-orange-700 dark:text-orange-300 mb-1">
                {t('transferVerification.notInTransferBoxes')}: {notInTransferBoxes.length}
              </p>
              <div className="space-y-1">
                {notInTransferBoxes.map((box, i) => (
                  <div key={i} className="text-xs text-orange-600 dark:text-orange-400 flex justify-between">
                    <span>{box.item_name || box.item_number}</span>
                    <span>{Number.isFinite(box.weight) ? box.weight.toFixed(2) : '—'} lbs</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer with buttons */}
        <div className="flex items-stretch gap-2 sm:gap-3 p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onGoBack}
            className="flex-1 basis-0 py-2.5 sm:py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 font-medium flex items-center justify-center gap-1.5 sm:gap-2 text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('transferVerification.goBackToScanning')}
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 basis-0 py-2.5 sm:py-3 rounded-lg font-medium flex items-center justify-center gap-1.5 sm:gap-2 text-sm sm:text-base text-white ${
              hasAnyDiscrepancy
                ? 'bg-amber-600 hover:bg-amber-700'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {t('transferVerification.confirmProceed')}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScanSummaryModal;
